// Mock in-memory + localStorage-backed store for the Rajnagar Gyanotsav app.
// All data is client-side. Roles: student (public), admin, teacher.

export type SubmissionStatus =
  | "Pending"
  | "Assigned for Verification"
  | "Not Proper"
  | "Ready for Reward";

export interface Teacher {
  id: string;
  name: string;
  email: string;
  mobile: string;
  subject: string;
  username: string;
  active: boolean;
}

export interface TimelineEvent {
  at: string;
  label: string;
}

export interface Submission {
  id: string; // reference number
  studentName: string;
  studentId: string;
  videoTitle: string;
  fileName: string;
  fileSizeMB: number;
  submittedAt: string;
  status: SubmissionStatus;
  assignedTeacherId?: string;
  assignedTeacherName?: string;
  assignedAt?: string;
  assignmentNotes?: string;
  teacherRemarks?: string;
  verifiedAt?: string;
  timeline: TimelineEvent[];
}

export interface AuthUser {
  role: "admin" | "teacher";
  id: string;
  name: string;
}

const K_TEACHERS = "rg_teachers";
const K_SUBS = "rg_submissions";
const K_AUTH = "rg_auth";

function isBrowser() {
  return typeof window !== "undefined";
}

const seedTeachers: Teacher[] = [
  { id: "T001", name: "Meera Patel", email: "meera@school.edu", mobile: "9876500001", subject: "Mathematics", username: "meera", active: true },
  { id: "T002", name: "Rakesh Shah", email: "rakesh@school.edu", mobile: "9876500002", subject: "Science", username: "rakesh", active: true },
  { id: "T003", name: "Nikita Joshi", email: "nikita@school.edu", mobile: "9876500003", subject: "Gujarati", username: "nikita", active: true },
  { id: "T004", name: "Arjun Desai", email: "arjun@school.edu", mobile: "9876500004", subject: "Social Studies", username: "arjun", active: true },
  { id: "T005", name: "Priya Trivedi", email: "priya@school.edu", mobile: "9876500005", subject: "English", username: "priya", active: false },
];

const sampleStudents = [
  ["Aanya Mehta", "S1001"], ["Kabir Vyas", "S1002"], ["Isha Rana", "S1003"],
  ["Dhruv Modi", "S1004"], ["Riya Bhatt", "S1005"], ["Vivaan Parekh", "S1006"],
  ["Anaya Solanki", "S1007"], ["Aarav Thakkar", "S1008"], ["Myra Dave", "S1009"],
  ["Reyansh Oza", "S1010"], ["Saanvi Pandya", "S1011"], ["Aditya Amin", "S1012"],
  ["Kiara Raval", "S1013"], ["Advik Gohil", "S1014"], ["Navya Chauhan", "S1015"],
  ["Vihaan Bhavsar", "S1016"], ["Ira Kapadia", "S1017"], ["Yug Jhaveri", "S1018"],
  ["Zara Sheth", "S1019"], ["Neel Vora", "S1020"],
];

const topics = [
  "Wonders of the Solar System", "Life of Mahatma Gandhi", "Photosynthesis Explained",
  "The Beauty of Kutch", "Pythagoras Theorem in Real Life", "Water Cycle Demo",
  "Gujarati Folk Traditions", "Fractions Made Easy", "History of Indus Valley",
  "Simple Machines at Home", "Grammar Basics", "Force and Motion",
  "Story of the Sabarmati", "Environmental Awareness", "Coding for Beginners",
  "Human Digestive System", "Art of Bandhani", "Renewable Energy",
  "Fun with Chemistry", "Poetic Devices",
];

function seedSubmissions(teachers: Teacher[]): Submission[] {
  const now = Date.now();
  const statuses: SubmissionStatus[] = ["Pending", "Assigned for Verification", "Not Proper", "Ready for Reward"];
  return sampleStudents.map(([name, sid], i) => {
    const status = statuses[i % 4];
    const submittedAt = new Date(now - (i + 1) * 3600_000 * 6).toISOString();
    const ref = `RGU-${String(2001 + i)}`;
    const timeline: TimelineEvent[] = [
      { at: submittedAt, label: "Submission created" },
      { at: submittedAt, label: "Status set to Pending" },
    ];
    let assignedTeacherId: string | undefined;
    let assignedTeacherName: string | undefined;
    let assignedAt: string | undefined;
    let teacherRemarks: string | undefined;
    let verifiedAt: string | undefined;
    if (status !== "Pending") {
      const t = teachers[i % teachers.length];
      assignedTeacherId = t.id;
      assignedTeacherName = t.name;
      assignedAt = new Date(new Date(submittedAt).getTime() + 3600_000).toISOString();
      timeline.push({ at: assignedAt, label: `Assigned to ${t.name}` });
      timeline.push({ at: assignedAt, label: "Status changed to Assigned for Verification" });
    }
    if (status === "Not Proper") {
      teacherRemarks = "Audio quality is poor and content not aligned with the topic.";
      verifiedAt = new Date(new Date(assignedAt!).getTime() + 7200_000).toISOString();
      timeline.push({ at: verifiedAt, label: "Teacher marked as Not Proper" });
    } else if (status === "Ready for Reward") {
      teacherRemarks = "Excellent presentation and clear explanation.";
      verifiedAt = new Date(new Date(assignedAt!).getTime() + 7200_000).toISOString();
      timeline.push({ at: verifiedAt, label: "Teacher marked as Ready for Reward" });
    }
    return {
      id: ref,
      studentName: name,
      studentId: sid,
      videoTitle: topics[i],
      fileName: `${sid}_video.mp4`,
      fileSizeMB: Math.round((15 + Math.random() * 70) * 10) / 10,
      submittedAt,
      status,
      assignedTeacherId,
      assignedTeacherName,
      assignedAt,
      teacherRemarks,
      verifiedAt,
      timeline,
    };
  });
}

function readLS<T>(key: string): T | null {
  if (!isBrowser()) return null;
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
}
function writeLS<T>(key: string, val: T) {
  if (!isBrowser()) return;
  localStorage.setItem(key, JSON.stringify(val));
}

let _teachers: Teacher[] | null = null;
let _subs: Submission[] | null = null;

export function getTeachers(): Teacher[] {
  if (_teachers) return _teachers;
  const existing = readLS<Teacher[]>(K_TEACHERS);
  _teachers = existing ?? seedTeachers;
  if (!existing) writeLS(K_TEACHERS, _teachers);
  return _teachers;
}
export function saveTeachers(t: Teacher[]) {
  _teachers = t;
  writeLS(K_TEACHERS, t);
  notify();
}

export function getSubmissions(): Submission[] {
  if (_subs) return _subs;
  const existing = readLS<Submission[]>(K_SUBS);
  _subs = existing ?? seedSubmissions(getTeachers());
  if (!existing) writeLS(K_SUBS, _subs);
  return _subs;
}
export function saveSubmissions(s: Submission[]) {
  _subs = s;
  writeLS(K_SUBS, s);
  notify();
}

// Simple subscription system so components can reactively update.
const listeners = new Set<() => void>();
export function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify() {
  listeners.forEach((l) => l());
}

// Auth — cache the parsed object so useSyncExternalStore gets a stable
// reference between calls (a fresh object each read would trigger React's
// infinite-loop warning and blow up the error boundary).
let _auth: AuthUser | null | undefined;
export function getAuth(): AuthUser | null {
  if (_auth !== undefined) return _auth;
  _auth = readLS<AuthUser>(K_AUTH);
  return _auth;
}
export function setAuth(a: AuthUser | null) {
  if (!isBrowser()) return;
  if (a) writeLS(K_AUTH, a);
  else localStorage.removeItem(K_AUTH);
  _auth = a;
  notify();
}

export function nextReferenceNumber(): string {
  const subs = getSubmissions();
  const maxN = subs.reduce((m, s) => {
    const n = parseInt(s.id.replace(/\D/g, ""), 10);
    return isNaN(n) ? m : Math.max(m, n);
  }, 2000);
  return `RGU-${maxN + 1}`;
}

export function addSubmission(input: Omit<Submission, "id" | "status" | "submittedAt" | "timeline">): Submission {
  const now = new Date().toISOString();
  const sub: Submission = {
    ...input,
    id: nextReferenceNumber(),
    status: "Pending",
    submittedAt: now,
    timeline: [
      { at: now, label: "Submission created" },
      { at: now, label: "Status set to Pending" },
    ],
  };
  const all = [sub, ...getSubmissions()];
  saveSubmissions(all);
  return sub;
}

export function assignTeacher(subIds: string[], teacherId: string, notes?: string) {
  const teacher = getTeachers().find((t) => t.id === teacherId);
  if (!teacher) return;
  const now = new Date().toISOString();
  const next = getSubmissions().map((s) => {
    if (!subIds.includes(s.id)) return s;
    return {
      ...s,
      assignedTeacherId: teacher.id,
      assignedTeacherName: teacher.name,
      assignedAt: now,
      assignmentNotes: notes,
      status: "Assigned for Verification" as SubmissionStatus,
      timeline: [
        ...s.timeline,
        { at: now, label: `${s.assignedTeacherId ? "Reassigned" : "Assigned"} to ${teacher.name}` },
        { at: now, label: "Status changed to Assigned for Verification" },
      ],
    };
  });
  saveSubmissions(next);
}

export function verifySubmission(id: string, status: "Not Proper" | "Ready for Reward", remarks: string, teacherName: string) {
  const now = new Date().toISOString();
  const next = getSubmissions().map((s) => {
    if (s.id !== id) return s;
    return {
      ...s,
      status,
      teacherRemarks: remarks,
      verifiedAt: now,
      timeline: [
        ...s.timeline,
        { at: now, label: `${teacherName} marked as ${status}` },
      ],
    };
  });
  saveSubmissions(next);
}

export function upsertTeacher(t: Teacher) {
  const list = getTeachers();
  const i = list.findIndex((x) => x.id === t.id);
  const next = i >= 0 ? list.map((x, idx) => (idx === i ? t : x)) : [...list, t];
  saveTeachers(next);
}

export function toggleTeacherActive(id: string) {
  saveTeachers(getTeachers().map((t) => (t.id === id ? { ...t, active: !t.active } : t)));
}

export function teacherLoad(teacherId: string) {
  return getSubmissions().filter((s) => s.assignedTeacherId === teacherId && s.status === "Assigned for Verification").length;
}
